import * as actionTypes from "../actionTypes";
import * as binderIds from "../enums/binderIds";
import * as binders from "../lib/api/binders";
import * as bindersActions from "./bindersActions";
import * as cacheManager from "../lib/cacheManager";
import * as pageActions from "./pageActions";
import * as query from "../lib/api/query";
import * as storageKeys from "../enums/storageKeys";
import * as tabContentKeys from "../enums/tabContentKeys";
import * as targetDisplayValues from "../enums/targetDisplayValues";
import * as tileContentKeys from "../enums/tileContentKeys";
import * as tileNames from "../enums/tileNames";
import * as tilesActions from "./tilesActions";
import * as tileSectionsActions from "./tileSectionsActions";
import find from "lodash.find";
import findIndex from "lodash.findindex";
import getApiTabToSave from "../lib/getApiTabToSave";
import getEmptyHorizontalBarChartContent from "../lib/getEmptyHorizontalBarChartContent";
import getEmptyPieChartContent from "../lib/getEmptyPieChartContent";
import getEmptyTimelineChartContent from "../lib/getEmptyTimelineChartContent";
import getSearchSortOrder from "../lib/getSearchSortOrder";
import initialContentValue from "../lib/initialContentValue";
import safeParseJSON from "../lib/safeParseJSON";
import safeSetItemLocalStorage from "../lib/safeSetItemLocalStorage";
import { getKey } from "../reducers/binderTabsReducer";

const EMPTY_OBJ = {};

export function setBinderTabs(tabs) {
	return {
		type: actionTypes.BINDER_TABS_SET,
		tabs,
	};
}

export function removeBinderTabs(binderId, tabId) {
	return (dispatch, getState) => {
		const state = getState();
		const key = getKey(binderId, tabId);
		const binderTab = state.binderTabs[key];

		const tileSectionIds =
			binderTab && binderTab.sectionIdNames
				? binderTab.sectionIdNames.map(idName => idName.id).filter(id => !!id)
				: [];
		const tileIds = tileSectionIds.reduce((acc, sectionId) => {
			const section = state.tileSections[sectionId];
			return !section || !section.tileIds ? acc : [...acc, ...section.tileIds];
		}, []);

		dispatch({
			type: actionTypes.BINDER_TABS_REMOVE,
			binderId,
			tabId,
			tileSectionIds,
			tileIds,
		});
	};
}

export function setBinderTabsTitle(binderId, tabId, title) {
	return {
		type: actionTypes.BINDER_TABS_SET_TITLE,
		binderId,
		tabId,
		title,
	};
}

export function setBinderTabsSearchTarget(binderId, tabId, search, title) {
	return {
		type: actionTypes.BINDER_TABS_SET_SEARCH_TARGET,
		binderId,
		tabId,
		search,
		title,
	};
}

export function setBinderTabsSearchFieldNames(binderId, tabId, fieldNames) {
	return {
		type: actionTypes.BINDER_TABS_SET_SEARCH_FIELD_NAMES,
		binderId,
		tabId,
		fieldNames,
	};
}

export function setBinderTabsProfile(binderId, tabId, libraryName, profileType, profileId, title) {
	return {
		type: actionTypes.BINDER_TABS_SET_PROFILE,
		binderId,
		tabId,
		libraryName,
		profileType,
		profileId,
		title,
	};
}

export function setBinderTabsSearchColOrder(binderId, tabId, columnOrder) {
	return {
		type: actionTypes.BINDER_TABS_SET_SEARCH_COL_ORDER,
		binderId,
		tabId,
		columnOrder,
	};
}

export function setBinderTabsSearchColWidth(binderId, tabId, columnWidths) {
	return {
		type: actionTypes.BINDER_TABS_SET_SEARCH_COL_WIDTH,
		binderId,
		tabId,
		columnWidths,
	};
}

export function setBinderTabsSearchSort(binderId, tabId, sortOrder) {
	return {
		type: actionTypes.BINDER_TABS_SET_SEARCH_SORT,
		binderId,
		tabId,
		sortOrder,
	};
}

export function setBinderTabsAnalyticsChartMeta(
	binderId,
	tabId,
	tablePercent,
	chartPercent,
	chartStyle,
	estimated,
	numbers,
) {
	return {
		type: actionTypes.BINDER_TABS_SET_ANAYLTICS_CHART_META,
		binderId,
		tabId,
		tablePercent,
		chartPercent,
		chartStyle,
		estimated,
		numbers,
	};
}

export function setBinderTabsAnalyticsTableMeta(binderId, tabId, tableMeta) {
	const { count, min, q0, q1, q2, q3, q4, max, average } = tableMeta;
	return {
		type: actionTypes.BINDER_TABS_SET_ANAYLTICS_TABLE_META,
		binderId,
		tabId,
		count,
		min,
		q0,
		q1,
		q2,
		q3,
		q4,
		max,
		average,
	};
}

export function setBinderTabsProfileSectionExpanded(binderId, tabId, sectionKey, expanded) {
	return {
		type: actionTypes.BINDER_TABS_SET_PROFILE_SECTION_EXPANDED,
		binderId,
		tabId,
		sectionKey,
		expanded,
	};
}

export function setBinderTabsDataWarningShown(binderId, tabId) {
	return {
		type: actionTypes.BINDER_TABS_SET_DATA_WARNING_SHOWN,
		binderId,
		tabId,
	};
}

export function setBinderTabsChartContent(binderId, tabId, chartContent) {
	return {
		type: actionTypes.BINDER_TABS_SET_CHART_CONTENT,
		binderId,
		tabId,
		chartContent,
	};
}

export function setBinderTabsTileContent(binderTabId, tileId, tileContent) {
	return {
		type: actionTypes.BINDER_TABS_SET_TILE_CONTENT,
		binderTabId,
		tileId,
		tileContent,
	};
}

export function showBinderTabDataWarning(binderId, tabId, message) {
	return (dispatch, getState) => {
		const state = getState();

		const key = getKey(binderId, tabId);
		if (!key) {
			return;
		}

		const tab = state.binderTabs[key];
		if (!tab || tab.dataWarningShown) {
			return;
		}

		let showWarning = true;
		if (tab.search) {
			const targetName = tab.search.target_name;
			const warnedTargetsStr = localStorage.getItem(storageKeys.DATA_WARNING_SHOWN);
			if (warnedTargetsStr !== null && warnedTargetsStr !== undefined) {
				const warnedTargets = safeParseJSON(warnedTargetsStr, EMPTY_OBJ);
				if (warnedTargets[targetName]) {
					showWarning = false;
				} else {
					safeSetItemLocalStorage(
						storageKeys.DATA_WARNING_SHOWN,
						JSON.stringify({ ...warnedTargets, [targetName]: true }),
					);
				}
			} else {
				safeSetItemLocalStorage(
					storageKeys.DATA_WARNING_SHOWN,
					JSON.stringify({ [targetName]: true }),
				);
			}
		}

		if (message && showWarning) {
			dispatch(pageActions.pageAddToast(message, null, 7000, true));
		}
		dispatch(setBinderTabsDataWarningShown(binderId, tabId));
	};
}

export function updateBinderTabSearch(binderId, tabId, search) {
	return (dispatch, getState) => {
		const state = getState();

		const binder = state.binders[binderId];
		if (!binder) {
			return Promise.resolve();
		}

		const tabKey = getKey(binderId, tabId);
		if (!tabKey) {
			return Promise.resolve();
		}

		const tab = state.binderTabs[tabKey];
		if (!tab) {
			return Promise.resolve();
		}

		const nextTarget = search && search.target_name ? state.targets[search.target_name] : null;
		if (!nextTarget || nextTarget.display !== targetDisplayValues.COMPARISON) {
			const updatedTab = {
				...tab,
				search,
				dirty: true,
			};
			dispatch(setBinderTabs([updatedTab]));
			return Promise.resolve();
		}

		return binders
			.getReport(binder.libraryName, nextTarget.name, search.criteria)
			.then(reportBinder => {
				dispatch(
					bindersActions.setBinder({
						...reportBinder,
						id: binder.id,
						tabs: reportBinder.tabs.map((t, i) => ({
							...t,
							binder_id: binder.id,
							client_id: i,
						})),
					}),
				);
			});
	};
}

export function setBinderTabsTileSections(binderTabId, sectionIdNames) {
	return {
		type: actionTypes.BINDER_TABS_SET_TILE_SECTIONS,
		binderTabId,
		sectionIdNames,
	};
}

export function addBinderTabsTileSection(binderTabId, sectionId, name) {
	return {
		type: actionTypes.BINDER_TABS_ADD_TILE_SECTION,
		binderTabId,
		sectionId,
		name,
	};
}

export function setBinderTabsTileSection(binderTabId, sectionId, name) {
	return (dispatch, getState) => {
		const state = getState();
		const binderTab = state.binderTabs[binderTabId];

		if (!binderTab) {
			return;
		}

		const sections = binderTab.sectionIdNames ? binderTab.sectionIdNames : [];
		const staleSectionIndex = findIndex(sections, section => section.name === name);
		if (staleSectionIndex === -1) {
			dispatch(addBinderTabsTileSection(binderTabId, sectionId, name));
			return;
		}

		const staleSectionId = sections[staleSectionIndex].id;
		if (staleSectionId && state.tileSections[staleSectionId]) {
			const exsitingSection = state.tileSections[staleSectionId];
			dispatch(tilesActions.removeTiles(exsitingSection.tileIds));
			dispatch(tileSectionsActions.removeTileSection(exsitingSection.id));
		}
		const nextSections = [
			...sections.slice(0, staleSectionIndex),
			{ id: sectionId, name },
			...sections.slice(staleSectionIndex + 1),
		];
		dispatch(setBinderTabsTileSections(binderTabId, nextSections));
	};
}

export function clearTileSections(binderTabId) {
	return (dispatch, getState) => {
		const state = getState();
		const binderTab = state.binderTabs[binderTabId];

		if (!binderTab || !binderTab.sectionIdNames || !binderTab.sectionIdNames.length) {
			return;
		}

		const tileSectionIds = binderTab.sectionIdNames.map(idName => idName.id).filter(id => !!id);
		const tileIds = tileSectionIds.reduce((acc, sectionId) => {
			const section = state.tileSections[sectionId];
			return !section || !section.tileIds ? acc : [...acc, ...section.tileIds];
		}, []);

		dispatch(tileSectionsActions.removeTileSections(tileSectionIds));
		dispatch(tilesActions.removeTiles(tileIds));
		dispatch(setBinderTabsTileSections(binderTabId, []));
	};
}

export function clearTileSection(binderTabId, sectionName) {
	return (dispatch, getState) => {
		const state = getState();
		const binderTab = state.binderTabs[binderTabId];

		if (!binderTab || !binderTab.sectionIdNames || !binderTab.sectionIdNames.length) {
			return;
		}

		const tileSectionIndex = binderTab.sectionIdNames.findIndex(
			idName => idName.name === sectionName,
		);
		if (tileSectionIndex === -1) {
			return;
		}

		const tileSectionId = binderTab.sectionIdNames[tileSectionIndex].id;
		const tileSection = tileSectionId ? state.tileSections[tileSectionId] : null;
		const tileIds = tileSection ? tileSection.tileIds : null;

		if (tileSectionId) {
			dispatch(tileSectionsActions.removeTileSections([tileSectionId]));
		}
		if (tileIds && tileIds.length) {
			dispatch(tilesActions.removeTiles(tileIds));
		}
		const nextSectionIdNames = [
			...binderTab.sectionIdNames.slice(0, tileSectionIndex),
			{ id: undefined, name: sectionName },
			...binderTab.sectionIdNames.slice(tileSectionIndex + 1),
		];
		dispatch(setBinderTabsTileSections(binderTabId, nextSectionIdNames));
	};
}

export function setSelectedTileControls(binderTabId, tileId, sectionName, selectedControls) {
	return (dispatch, getState) => {
		const state = getState();
		const binderTab = state.binderTabs[binderTabId];

		if (!binderTab || !binderTab.sectionIdNames || !binderTab.sectionIdNames.length) {
			return;
		}

		const sectionIdName = find(binderTab.sectionIdNames, idName => idName.name === sectionName);
		if (!sectionIdName || !state.tileSections[sectionIdName.id]) {
			return;
		}

		const tileIds = state.tileSections[sectionIdName.id].tileIds;
		if (!tileIds || !tileIds.length) {
			return;
		}

		const tile = find(tileIds.map(id => state.tiles[id]), curTile => curTile.id === tileId);
		if (!tile) {
			return;
		}

		let defaultContent = {};
		if (tile.name === tileNames.TIMELINES) {
			defaultContent = getEmptyTimelineChartContent();
		} else if (tile.name === tileNames.HORIZONTAL_BAR) {
			defaultContent = getEmptyHorizontalBarChartContent();
		} else if (tile.name === tileNames.PIE) {
			defaultContent = getEmptyPieChartContent();
		}

		const currentTileContent =
			binderTab.content && binderTab.content[tabContentKeys.TILES_CONTENT]
				? binderTab.content[tabContentKeys.TILES_CONTENT][tileId]
				: undefined;

		const content = {
			...defaultContent,
			...currentTileContent,
			[tileContentKeys.CONTROLS]: selectedControls,
		};

		return cacheManager.clearSearchGrid(binderTab.binderId, binderTab.id, tileId).then(() => {
			dispatch(setBinderTabsTileContent(binderTabId, tileId, content));
			dispatch(clearTileSection(binderTabId, sectionName));
		});
	};
}

export function addTableauFilter(binderId, tabId, filter) {
	const binderTabId = getKey(binderId, tabId);
	return {
		type: actionTypes.BINDER_TABS_ADD_TABLEAU_FILTER,
		binderTabId,
		filter,
	};
}

export function addTableauParameter(binderId, tabId, parameter) {
	const binderTabId = getKey(binderId, tabId);
	return {
		type: actionTypes.BINDER_TABS_ADD_TABLEAU_PARAMETER,
		binderTabId,
		parameter,
	};
}

export function resetTableauValues(binderId, tabId) {
	return (dispatch, getState) => {
		const state = getState();

		const binderTabId = getKey(binderId, tabId);
		const binderTab = state.binderTabs[binderTabId];
		const binder = state.binders[binderId];

		if (!binder || !binderTab || Number.isFinite(binderTab.clientId)) {
			return Promise.resolve();
		}

		const filters = initialContentValue(binderTab, tabContentKeys.TABLEAU_FILTERS, null);
		const parameters = initialContentValue(binderTab, tabContentKeys.TABLEAU_PARAMETERS, null);
		if ((!filters || !filters.length) && (!parameters || !parameters.length)) {
			return Promise.resolve();
		}

		dispatch({
			type: actionTypes.BINDER_TABS_RESET_TABLEAU_VALUES,
			binderTabId,
		});

		if (binderId === binderIds.UNASSIGNED) {
			return Promise.resolve();
		}

		return binders.getBinder(binder.libraryName, binder.type, binderId).then(apiBinder => {
			const apiTab = tabId < apiBinder.tabs.length ? { ...apiBinder.tabs[tabId] } : null;
			if (!apiTab) {
				return;
			}
			const savedFilters = initialContentValue(apiTab, tabContentKeys.TABLEAU_FILTERS, null);
			const savedParameters = initialContentValue(
				apiTab,
				tabContentKeys.TABLEAU_PARAMETERS,
				null,
			);
			if (
				(!savedFilters || !savedFilters.length) &&
				(!savedParameters || !savedParameters.length)
			) {
				return;
			}

			delete apiTab.content[tabContentKeys.TABLEAU_FILTERS];
			delete apiTab.content[tabContentKeys.TABLEAU_PARAMETERS];
			const tabs = [
				...apiBinder.tabs.slice(0, tabId),
				apiTab,
				...apiBinder.tabs.slice(tabId + 1),
			].map(getApiTabToSave);

			return binders.update(binderId, tabs);
		});
	};
}

export function hideResultRows(binderId, tabId, hideRowIds, visibleRowIds, isAllSelected, total) {
	return (dispatch, getState) => {
		const state = getState();

		const binderTabId = getKey(binderId, tabId);
		const binderTab = state.binderTabs[binderTabId];
		const binder = state.binders[binderId];

		if (!binder || !binderTab) {
			return Promise.resolve();
		}

		if (isAllSelected && !visibleRowIds.length) {
			dispatch({
				type: actionTypes.BINDER_TABS_SET_ALL_ROWS_HIDDEN,
				binderTabId,
			});
			return Promise.resolve();
		}

		let loadRowIds = Promise.resolve(hideRowIds);
		if (isAllSelected) {
			const initialSortOrder = initialContentValue(
				binderTab,
				tabContentKeys.SORT_ORDER_KEY,
				null,
			);
			const targetName = binderTab.search.target_name;
			const target = targetName ? state.targets[targetName] : null;
			const fields = target ? target.fieldIds.map(id => state.fields[id]) : [];

			const sortOrder = getSearchSortOrder(
				binderTab.search.field_names,
				fields,
				initialSortOrder,
			);
			loadRowIds = query
				.grid(binderTab.search, sortOrder, 0, total, binderId, tabId)
				.then(results => {
					const hiddenIds = results
						.filter(result => !visibleRowIds.some(id => id === result.record_id))
						.map(r => r.record_id);
					return hiddenIds;
				});
		}

		return loadRowIds.then(rowIds => {
			dispatch({
				type: actionTypes.BINDER_TABS_SET_ROWS_HIDDEN,
				binderTabId,
				rowIds,
			});
		});
	};
}

export function clearHiddenRows(binderId, tabId) {
	return (dispatch, getState) => {
		const state = getState();

		const binderTabId = getKey(binderId, tabId);
		const binderTab = state.binderTabs[binderTabId];
		const binder = state.binders[binderId];

		if (!binder || !binderTab) {
			return;
		}

		dispatch({
			type: actionTypes.BINDER_TABS_RESET_HIDDEN_ROWS,
			binderTabId,
		});
	};
}

export function toggleResultsSelectIsHidden(binderId, tabId) {
	return (dispatch, getState) => {
		const state = getState();

		const binderTabId = getKey(binderId, tabId);
		const binderTab = state.binderTabs[binderTabId];
		const binder = state.binders[binderId];

		if (!binder || !binderTab) {
			return;
		}

		dispatch({
			type: actionTypes.BINDER_TABS_TOGGLE_SELECT_HIDDEN,
			binderTabId,
		});
	};
}
